/**
 * CLE-2811 — the AI's reply while a pass runs: the last item of the conversation, written as it arrives.
 *
 * It shows the worker's streamed narration (`progress.narration`, remembered by `rememberNarration` so it
 * survives the worker clearing its progress at deposit) with a blinking cursor, formatted like any other
 * reply. A pass that streams no text — the fast facts pipeline reports steps instead — shows its activity
 * feed (`progress.activity`, remembered by `rememberActivity`), newest step last. Before either:
 * "Thinking…" and how long the pass has been going (`liveReplyContent` picks). When the pass ends, the
 * stored reply takes this bubble's place — same position, same styling.
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { DraftRunStatus } from "../../../store/queries/contractModelDrafts";
import { LIVE_REPLY_ARIA, LIVE_REPLY_THINKING } from "../copy";
import { blink } from "../progress/styles";
import { formatCounter, generationElapsedMs, isProcessing } from "../utils";
import ChatMarkdown from "./chatMarkdown";
import { liveReplyContent } from "./conversation";

const Cursor = styled.span`
  display: inline-block;
  width: 0.45em;
  height: 1em;
  margin-left: 0.15em;
  vertical-align: text-bottom;
  border-radius: 1px;
  background: var(--primary-color);
  animation: ${blink} 1.1s steps(1) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.6;
  }
`;

const Thinking = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--text-color-secondary);
  font-variant-numeric: tabular-nums;
`;

/** The pipeline's steps, one per line, muted like the "Thinking…" line they replace; the newest is last. */
const Activity = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--text-color-secondary);
  word-break: break-word;
  overflow-wrap: anywhere;

  li:last-child {
    color: var(--text-color);
  }
`;

interface ILiveReplyProps {
  /** The narration so far; "" before the first words (or from a worker without narration). */
  text: string;
  /** CLE-2811 — the worker's last actions, shown when there is no narration (the fast facts pipeline). */
  activity?: string[];
  run: { status: DraftRunStatus; startedAt: string | null; updatedAt: string };
}

function LiveReply({ text, activity, run }: ILiveReplyProps) {
  const content = liveReplyContent(text, activity);
  const thinking = content.kind === "thinking";
  // Only the "Thinking… 12 s" counter needs a clock; the text arrives with the page's poll.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!thinking) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [thinking]);

  if (content.kind === "narration") {
    return (
      <div aria-label={LIVE_REPLY_ARIA} aria-busy="true">
        <ChatMarkdown
          text={content.text.trimEnd()}
          trailing={<Cursor aria-hidden />}
        />
      </div>
    );
  }
  if (content.kind === "activity") {
    return (
      <Activity aria-label={LIVE_REPLY_ARIA} aria-busy="true">
        {content.lines.map((line, i) => (
          // Lines repeat ("Reading page 3…" twice) and the list slides, so the index is the identity.
          // eslint-disable-next-line react/no-array-index-key
          <li key={i}>
            {line}
            {i === content.lines.length - 1 ? <Cursor aria-hidden /> : null}
          </li>
        ))}
      </Activity>
    );
  }
  // Between a Regenerate click and the next poll the run still reads as finished; its clock would be the
  // previous pass's, so say nothing rather than something wrong.
  const elapsed = isProcessing(run.status)
    ? generationElapsedMs(run, now)
    : null;
  return (
    <Thinking aria-label={LIVE_REPLY_ARIA} aria-busy="true">
      <i className="pi pi-spin pi-spinner" style={{ fontSize: "0.85rem" }} />
      {LIVE_REPLY_THINKING}
      {elapsed != null ? <span>{formatCounter(elapsed)}</span> : null}
    </Thinking>
  );
}

export default LiveReply;
